import React, {useRef, useState} from "react";
import {
	Fade,
	ClickAwayListener,
	FormHelperText,
	IconButton,
	InputAdornment,
	ListItemAvatar,
	ListItemButton,
	ListItemText,
	OutlinedInput,
	Popover,
	Tooltip,
	Typography
} from "@mui/material";
import {Box} from "@mui/system";
import StaticAvatar from "../../StaticAvatar";
import {useTranslation} from "react-i18next";
import {filterArrayObjectValues} from "../../../utils";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircleExclamation, faCircleXmark, faTrashUndo} from "@fortawesome/pro-solid-svg-icons";

const EmptySearch = ({searchString}) => {
	const {t} = useTranslation();
	return (
		<Box sx={{p: 2, display: "flex", alignItems: "center", justifyContent: "center"}}>
			<FontAwesomeIcon icon={faCircleExclamation} size={"2xl"} />
			<Box sx={{display: "flex", flexDirection: "column", ml: 2}}>
				<Typography variant="h6">
					{t("Hmmm...")}
				</Typography>
				<Typography variant="subtitle1">
					{t("We couldn't find any matches for")}{" "}{searchString}
				</Typography>
			</Box>
		</Box>
	);
};

const SearchField = ({users, participants, handleParticipantClick, handleResetParticipantsClick, error}) => {
	const [open, setOpen] = useState(false);
	const [searchString, setSearchString] = useState("");
	const inputContainerRef = useRef(null);
	const inputRef = useRef(null);
	const { t } = useTranslation();

	const handleClickAway = () => {
		setOpen(false);
	};

	const handleSearchChange = (value) => {
		if (!open) {
			setOpen(true);
		}
		setSearchString(value);
	};

	const preserveInputFocus = (event, cb) => {
		event.preventDefault();
		event.stopPropagation();
		cb();
		inputRef.current.focus();
	};

	const onResetParticipantsClick = (event) => {
		preserveInputFocus(event, () => handleResetParticipantsClick());
	};

	const onUserClick = (event, user) => {
		preserveInputFocus(event, () => handleParticipantClick(user));
	};

	const handleClearInput = (event) => {
		preserveInputFocus(event, () => setSearchString(""));
	};

	const handleInputClick = (event) => {
		preserveInputFocus(event, () => setOpen(!open));
	};

	const trimmedUsers = filterArrayObjectValues(
		users,
		["firstName", "lastName", "username"],
		searchString
	);

	return (
		<Box sx={{py: 1}}>
			<Box sx={{display: "flex", flexDirection: "row", justifyContent: "space-between", pb: 1}}>
				<Box sx={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center"}}>
					<Box sx={{display: "flex", flexDirection: "column"}}>
						<Typography variant="h6">
							{t("Add participants")}
						</Typography>
						<Typography variant="subtitle1">
							{t("Search for users by name or username")}
						</Typography>
					</Box>
					<Tooltip placement="top" arrow title={t("Click the search field to show/hide all users")}>
						<FontAwesomeIcon icon={faCircleExclamation} size={"xl"}/>
					</Tooltip>
				</Box>
				<Tooltip arrow placement="top" title={t("Clear selected participants")}>
					<Box sx={{display: "flex", justifyContent: "center", alignItems: "center"}}>
						<IconButton
							sx={{zIndex: 1301, borderRadius: "50%"}}
							onClick={onResetParticipantsClick}
							disabled={participants.length === 0}
						>
							<FontAwesomeIcon icon={faTrashUndo}	/>
						</IconButton>
					</Box>
				</Tooltip>
			</Box>
			<OutlinedInput
				aria-owns="users-popover"
				aria-haspopup="true"
				autoComplete="off"
				id="participants-search-input"
				error={error}
				inputRef={inputRef}
				ref={inputContainerRef}
				variant="outlined"
				onChange={(e) => handleSearchChange(e.target.value)}
				onClick={handleInputClick}
				value={searchString}
				fullWidth
				placeholder={t("Search")}
				aria-label={"Search"}
				sx={{zIndex: 1301}}
				endAdornment={searchString.length > 0 &&
					<InputAdornment position="end">
						<IconButton
							sx={{p: "10px", zIndex: 1301, borderRadius: "50%"}}
							aria-label="clear"
							onClick={handleClearInput}
							size="small"
						>
							<FontAwesomeIcon icon={faCircleXmark} />
						</IconButton>
					</InputAdornment>
				}
			/>
			{error &&
				<FormHelperText error id="participants-search-input">{t("No participants selected")}</FormHelperText>
			}
			<Popover
				TransitionComponent={Fade}
				id="users-popover"
				hideBackdrop
				disableAutoFocus
				disableEnforceFocus
				disableRestoreFocus
				disablePortal
				onClose={handleClickAway}
				open={open}
				anchorEl={inputContainerRef.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "center"
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "center"
				}}
				slotProps={{
					paper: {
						elevation: 3,
						sx: {width: "460px", mt: 1}
					}
				}}
			>
				<ClickAwayListener onClickAway={handleClickAway}>
					<Box sx={{display: "flex", flexWrap: "wrap", flexDirection: "row", maxHeight: "300px"}}>
						{trimmedUsers.length === 0 &&
							<EmptySearch searchString={searchString}/>
						}
						{trimmedUsers.length > 0 && trimmedUsers.map((user, key) => (
							<ListItemButton
								selected={participants.some((participant) => participant.id === user.id)}
								key={key}
								value={user}
								onClick={(event) => onUserClick(event, user)}
								sx={{width: "100%", borderRadius: 0}}
							>
								<ListItemAvatar>
									<StaticAvatar
										id={user.id}
										initials={String(user.firstName)[0] + String(user.lastName)[0]}
										user={user}
									/>
								</ListItemAvatar>
								<ListItemText
									primary={user.firstName + " " + user.lastName}
									secondary={user.username}
								/>
							</ListItemButton>
						))}
					</Box>
				</ClickAwayListener>
			</Popover>

		</Box>
	);
};
export default SearchField;
